/**
 * @fileoverview Empty State Card component.
 * @summary Displays an empty state message with optional action button.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/EmptyStateCard
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { type ReactNode } from 'react'

interface EmptyStateCardProps {
  icon?: string | ReactNode
  title: string
  description: string
  actionLabel?: string
  onAction?: () => void
  isClickable?: boolean
}

/**
 * @summary Empty state card component.
 * @param {EmptyStateCardProps} props - Component props.
 * @returns {React.ReactElement} Empty state card.
 */
export function EmptyStateCard({
  icon = '📋',
  title,
  description,
  actionLabel,
  onAction,
  isClickable = false,
}: EmptyStateCardProps) {
  const cardClasses = `card text-center py-16 ${isClickable ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`

  return (
    <div className={cardClasses} onClick={isClickable ? onAction : undefined}>
      <div className="text-6xl mb-6">
        {typeof icon === 'string' ? icon : icon}
      </div>
      <h3 className="text-2xl font-bold mb-3 text-(--color-text-primary)">{title}</h3>
      <p className="text-(--color-text-secondary) text-lg mb-8 max-w-md mx-auto">
        {description}
      </p>
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="btn btn-primary btn-lg mb-4"
        >
          {actionLabel}
        </button>
      )}
    </div>
  )
}

